var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useNavigate, useLocation } from 'react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { HandshakePainterServiceApiV1, basePath } from 'src/api/handshakeApi';
import { useDefaultApiParams } from './useDefaultApiParams';
import { useAnalyticsContext } from 'src/common/metrics';
import { isPaymentError } from 'src/utils/paymentErrors';
import { routes } from 'src/utils/routes';
const PREPARE_PAYMENT_PATH = '/painter/api/checkout/v1/prepare-payment';
const START_WIDGET_CONTEXT_ID_PARAM = 'startWidgetContextId';
const MARKETPLACE_ID_PARAM = 'marketplaceId';
/**
 * Hook to confirm order on page load (useQuery-based).
 * Used by ThankYouPageConfirm in control group (weblab C).
 */
export const useConfirmOrder = (request) => {
    const defaultApiParams = useDefaultApiParams().getDefaultParams();
    return useQuery({
        queryKey: ['confirmOrder', request, defaultApiParams],
        queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
            return yield HandshakePainterServiceApiV1.confirmOrder({
                confirmOrderRequest: request,
                antiCsrftokenA2z: defaultApiParams.antiCsrftokenA2z,
                xJoinId: defaultApiParams.xJoinId,
                xUserLinked: defaultApiParams.xUserLinked,
                marketplaceId: defaultApiParams.marketplaceId,
            });
        }),
    });
};
/**
 * Hook to confirm order on demand (useMutation-based) for the treatment group (weblab T1).
 * Handles all routing based on the confirmOrder response:
 * - startWidgetContextId → navigate to payment verification page for MFA challenge
 * - errorCode with payment error → payment-failed page
 * - errorCode with other error → generic dog page inline
 * - success → navigate to ThankYouPage
 * Callers that stay on the page (1-Click checkout sheet) pass onOrderConfirmed /
 * onPaymentError to replace the success and payment-failed navigation.
 */
export const useConfirmOrderMutation = ({ onGenericError, onSettled, onMfaRedirect, onOrderConfirmed, onPaymentError, }) => {
    const apiParams = useDefaultApiParams();
    const navigate = useNavigate();
    const location = useLocation();
    const rumClient = useAnalyticsContext();
    return useMutation({
        mutationFn: (request) => __awaiter(void 0, void 0, void 0, function* () {
            if (!request.purchaseId) {
                // purchaseId should always be present — throw so onError records it and shows the dog page
                throw new Error('useConfirmOrderMutation: mutationFn called without purchaseId');
            }
            const defaultApiParams = apiParams.getDefaultParams();
            return yield HandshakePainterServiceApiV1.confirmOrder({
                confirmOrderRequest: request,
                antiCsrftokenA2z: defaultApiParams.antiCsrftokenA2z,
                xJoinId: defaultApiParams.xJoinId,
                xUserLinked: defaultApiParams.xUserLinked,
                marketplaceId: defaultApiParams.marketplaceId,
            });
        }),
        onSuccess: (response, { purchaseId }) => {
            var _a;
            // MFA required — full-page redirect to the backend's prepare-payment HTML page
            if (response.startWidgetContextId) {
                rumClient.recordEvent('MFA_REDIRECT_INITIATED', { purchaseId });
                const preparePaymentUrl = new URL(PREPARE_PAYMENT_PATH, basePath);
                preparePaymentUrl.searchParams.set(START_WIDGET_CONTEXT_ID_PARAM, response.startWidgetContextId);
                const defaultApiParams = apiParams.getDefaultParams();
                preparePaymentUrl.searchParams.set(MARKETPLACE_ID_PARAM, defaultApiParams.marketplaceId);
                // Signal the redirect is starting so callers can stay in a processing
                // state until the browser navigates away (isPending flips false now).
                onMfaRedirect === null || onMfaRedirect === void 0 ? void 0 : onMfaRedirect();
                window.location.href = preparePaymentUrl.toString();
                return;
            }
            // If there's an error in the response, route to appropriate error page
            const errorCode = (_a = response.error) === null || _a === void 0 ? void 0 : _a.errorCode;
            if (errorCode) {
                if (isPaymentError(errorCode)) {
                    rumClient.recordEvent('PAYMENT_ERROR_DETECTED', {
                        purchaseId,
                        errorCode,
                    });
                    if (onPaymentError) {
                        onPaymentError(errorCode);
                    }
                    else {
                        navigate(`${routes.paymentFailedPage}${location.search}`);
                    }
                }
                else {
                    rumClient.recordEvent('CONFIRM_ORDER_GENERIC_ERROR', {
                        purchaseId,
                        errorCode,
                    });
                    onGenericError();
                }
                return;
            }
            if (onOrderConfirmed) {
                onOrderConfirmed(purchaseId);
                return;
            }
            // Success — navigate to ThankYouPage
            navigate(`${routes.thankYouPage}${location.search}`, {
                state: { purchaseId },
            });
        },
        onError: (error, { purchaseId }) => {
            // Network or unexpected error — show dog page inline
            rumClient.recordError(error);
            rumClient.recordEvent('CONFIRM_ORDER_GENERIC_ERROR', { purchaseId });
            onGenericError();
        },
        onSettled,
    });
};
